import React, { useState } from "react";
import { Check, X, UserX, UserCheck, Gift, TrendingDown, Loader2, ListChecks, RefreshCw } from "lucide-react";

// Contextual toolbar that appears above the payroll table once at least one
// row is selected. Everything here operates on the WHOLE selection (which
// may span pages — see selectedMeta in index.jsx), never just the visible page.
//
// Attendance button is state-aware: the selection snapshot carries each
// row's isIgnored flag, so the bar offers "Ignore" when none are ignored,
// "Restore" when all are, and both when the selection is mixed.

const ACCENTS = {
  primary: "#2e7cba",
  amber: "#f59e0b",
  emerald: "#10b981",
  rose: "#ef4444",
};

const ActionButton = ({ icon: Icon, label, count, accent, onClick, disabled, busy, isDark }) => {
  const [hover, setHover] = useState(false);
  const active = hover && !disabled;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
        height: "40px",
        paddingBlock: 0,
        paddingInlineStart: "6px",
        paddingInlineEnd: "14px",
        borderRadius: "12px",
        border: `1px solid ${active ? `${accent}80` : isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)"}`,
        background: active ? `${accent}1f` : isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
        color: isDark ? "#e2e8f0" : "#1e293b",
        fontSize: "13.5px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled && !busy ? 0.5 : 1,
        transform: active ? "translateY(-1px)" : "none",
        boxShadow: active ? `0 6px 16px ${accent}26` : "none",
        transition: "all 0.18s ease",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: `${accent}${isDark ? "29" : "1a"}`,
          color: accent,
          flexShrink: 0,
        }}
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} strokeWidth={2.25} />}
      </span>
      {label}
      {count != null && (
        <span
          style={{
            minWidth: "22px",
            height: "20px",
            padding: "0 6px",
            borderRadius: "999px",
            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
            color: isDark ? "#cbd5e1" : "#475569",
            fontSize: "11.5px",
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
};

const PayrollBulkActionBar = ({
  theme,
  isRTL,
  selectedCount,
  totalCount = 0,
  allSelected,
  selectAllBusy,
  onSelectAll,
  ignoredCount = 0,
  paidCount = 0,
  onClearSelection,
  onBulkIgnore,
  onBulkUnignore,
  onOpenBulkAllowance,
  onOpenBulkDeduction,
  onOpenBulkRecreate,
  bulkIgnoreBusy,
  isAdmin,
  modulePermissions,
}) => {
  if (!selectedCount) return null;

  const isDark = theme === "dark";
  const notIgnoredCount = selectedCount - ignoredCount;
  const canIgnore = isAdmin || modulePermissions?.ignoreAttendance;
  const canAllowance = isAdmin || modulePermissions?.employeeAllowances;
  const canDeduction = isAdmin || modulePermissions?.employeeDeductions;
  const canProcess = isAdmin || modulePermissions?.processPayroll;
  const canRecreate = isAdmin || modulePermissions?.recreatePayroll;
  const showSelectAll = !allSelected && totalCount > selectedCount;

  const L = (ar, en) => (isRTL ? ar : en);

  const metaParts = [];
  if (totalCount > 0) metaParts.push(L(`من أصل ${totalCount} موظف`, `of ${totalCount} employees`));
  if (ignoredCount > 0) metaParts.push(L(`${ignoredCount} متجاهل حضورهم`, `${ignoredCount} attendance ignored`));
  if (paidCount > 0) metaParts.push(L(`${paidCount} مدفوع (مقفول)`, `${paidCount} paid (locked)`));

  const muted = isDark ? "#94a3b8" : "#64748b";

  return (
    <div
      role="toolbar"
      aria-label={L("إجراءات جماعية على الموظفين المحددين", "Bulk actions for selected employees")}
      style={{
        position: "sticky",
        top: "12px",
        zIndex: 20,
        margin: "16px 0 20px 0",
        padding: "12px 14px",
        borderRadius: "16px",
        background: isDark
          ? "linear-gradient(135deg, rgba(30,41,59,0.96) 0%, rgba(15,23,42,0.96) 100%)"
          : "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
        border: `1px solid ${isDark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.22)"}`,
        borderInlineStart: `4px solid ${ACCENTS.primary}`,
        boxShadow: isDark ? "0 10px 30px rgba(0,0,0,0.45)" : "0 10px 28px rgba(46,124,186,0.12)",
        backdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "12px 16px",
        animation: "fadeInUp 0.2s ease-out",
      }}
    >
      {/* Selection summary */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: ACCENTS.primary,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 6px 14px ${ACCENTS.primary}55`,
            flexShrink: 0,
          }}
        >
          <Check size={20} strokeWidth={3} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "6px",
              color: isDark ? "#f8fafc" : "#0f172a",
              fontWeight: 700,
              fontSize: "15px",
              lineHeight: 1.2,
            }}
          >
            <span style={{ fontSize: "20px", fontVariantNumeric: "tabular-nums" }}>{selectedCount}</span>
            <span>{L("موظف محدد", selectedCount === 1 ? "employee selected" : "employees selected")}</span>
          </div>
          {metaParts.length > 0 && (
            <div style={{ fontSize: "12px", color: muted, marginTop: "3px" }}>{metaParts.join(" · ")}</div>
          )}
        </div>

        {showSelectAll && (
          <>
            <span
              aria-hidden
              style={{ width: "1px", height: "30px", background: isDark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.1)" }}
            />
            <button
              type="button"
              onClick={onSelectAll}
              disabled={selectAllBusy}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "transparent",
                border: "none",
                padding: "6px 4px",
                color: ACCENTS.primary,
                fontSize: "13px",
                fontWeight: 700,
                cursor: selectAllBusy ? "wait" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {selectAllBusy ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={15} />}
              {L(`تحديد الكل (${totalCount})`, `Select all ${totalCount}`)}
            </button>
          </>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        {canIgnore && notIgnoredCount > 0 && (
          <ActionButton
            icon={UserX}
            accent={ACCENTS.amber}
            isDark={isDark}
            label={ignoredCount > 0 ? L("تجاهل الحضور للباقي", "Ignore for the rest") : L("تجاهل الحضور", "Ignore attendance")}
            count={notIgnoredCount}
            onClick={onBulkIgnore}
            disabled={bulkIgnoreBusy}
            busy={bulkIgnoreBusy}
          />
        )}
        {canIgnore && ignoredCount > 0 && (
          <ActionButton
            icon={UserCheck}
            accent={ACCENTS.primary}
            isDark={isDark}
            label={L("إلغاء تجاهل الحضور", "Restore attendance")}
            count={ignoredCount}
            onClick={onBulkUnignore}
            disabled={bulkIgnoreBusy}
            busy={bulkIgnoreBusy}
          />
        )}
        {canAllowance && (
          <ActionButton
            icon={Gift}
            accent={ACCENTS.emerald}
            isDark={isDark}
            label={L("إضافة مكافأة / بدل", "Add bonus / allowance")}
            onClick={onOpenBulkAllowance}
          />
        )}
        {canDeduction && (
          <ActionButton
            icon={TrendingDown}
            accent={ACCENTS.rose}
            isDark={isDark}
            label={L("إضافة خصم", "Add deduction")}
            onClick={onOpenBulkDeduction}
          />
        )}
        {canRecreate && onOpenBulkRecreate && (
          <ActionButton
            icon={RefreshCw}
            accent={ACCENTS.primary}
            isDark={isDark}
            label={L("إعادة إنشاء المرتب", "Recreate Payroll")}
            onClick={onOpenBulkRecreate}
          />
        )}
      </div>
    </div>
  );
};

export default PayrollBulkActionBar;
